import { X, CreditCard } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { CpnPaymentMethod, getPaymentMethodInfo } from '@/utils/cpn'

interface PaymentMethodSelectorProps {
  paymentMethods: CpnPaymentMethod[]
  selectedPaymentMethod?: CpnPaymentMethod
  currencyCode: string
  countryName: string
  flag: string
  onSelect: (paymentMethod: CpnPaymentMethod) => void
  onClose: () => void
}

const PaymentMethodSelector = ({
  paymentMethods,
  selectedPaymentMethod,
  currencyCode,
  countryName,
  flag,
  onSelect,
  onClose,
}: PaymentMethodSelectorProps) => {
  // Sort payment methods by speed (fastest first)
  const sortedPaymentMethods = [...paymentMethods].sort((a, b) => {
    const speedA = getPaymentMethodInfo(a).speed || 0
    const speedB = getPaymentMethodInfo(b).speed || 0
    return speedB - speedA
  })

  // Helper function to get speed label
  const getSpeedLabel = (speed: number) => {
    if (speed >= 9) return 'Instant'
    if (speed >= 7) return 'Same Day'
    if (speed >= 5) return 'Fast'
    return 'Standard'
  }

  return (
    <div
      className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <Card
        className="w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-lg border-0 overflow-hidden animate-in fade-in-0 zoom-in-95 duration-200 mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-600">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Payment Method
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-full"
            >
              <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </Button>
          </div>
        </div>

        {/* Currency Info */}
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700 border-b border-gray-100 dark:border-gray-600">
          <div className="flex items-center space-x-3">
            <span className="text-xl">{flag}</span>
            <div className="flex-1">
              <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                Sending to
              </div>
              <div className="font-medium text-gray-900 dark:text-white">
                {currencyCode} • {countryName}
              </div>
            </div>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="p-4 max-h-60 overflow-y-auto">
          <div className="space-y-2">
            {sortedPaymentMethods.map((method, index) => {
              const methodInfo = getPaymentMethodInfo(method)
              const isSelected = selectedPaymentMethod === method
              const isFastest = index === 0 && sortedPaymentMethods.length > 1
              const speedLabel = getSpeedLabel(methodInfo.speed || 0)

              return (
                <button
                  key={method}
                  onClick={() => {
                    onSelect(method)
                    onClose()
                  }}
                  className={`w-full flex items-center p-3 rounded-lg transition-all duration-150 border ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400'
                      : 'border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className="w-10 h-10 rounded-lg bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-600 flex items-center justify-center mr-3 flex-shrink-0">
                    {methodInfo.logo ? (
                      <img
                        src={methodInfo.logo}
                        alt={methodInfo.name}
                        className="w-7 h-7 object-contain"
                      />
                    ) : (
                      <CreditCard className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    )}
                  </div>

                  <div className="text-left flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="font-medium text-gray-900 dark:text-white text-sm truncate">
                          {methodInfo.name}
                        </span>
                      </div>
                      {isSelected && (
                        <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                          <svg
                            className="w-3 h-3 text-white"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {methodInfo.description && (
                        <div className="text-xs text-gray-600 dark:text-gray-400 truncate">
                          {methodInfo.description}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      </Card>
    </div>
  )
}

export default PaymentMethodSelector
